import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Ruler, Wallet, CalendarClock, ShieldCheck, Calculator } from "lucide-react";
import PageShell from "@/components/PageShell";
import { IMAGES } from "@/lib/config";

const reasons = [
  {
    icon: Ruler,
    title: "Progetti rigorosamente su misura",
    desc: "Su tua richiesta, possiamo sviluppare soluzioni personalizzate modellate sulle tue reali esigenze abitative e sul tuo gusto. Questo con l'aiuto di un nostro Interior Design e architetto che studierà i tuoi bisogni, le tue passioni, il tuo modo di vivere gli spazi e progetterà una casa su misura per te.",
  },
  {
    icon: Wallet,
    title: "Costi definitivi e bloccati",
    desc: "Garantiamo la trasparenza economica fin dall'inizio, assicurando che non ci saranno aumenti improvvisi durante l'esecuzione dei lavori. A patto che non ci siano vizi occulti nella struttura e che tu non voglia modificare radicalmente il progetto in itinere.",
  },
  {
    icon: CalendarClock,
    title: "Tempi di consegna pattuiti a contratto",
    desc: "Fissiamo la data di ultimazione dei lavori all'interno del contratto e paghiamo penali se questa data non è stata rispettata per colpa nostra.",
  },
  {
    icon: ShieldCheck,
    title: "Tutela contrattuale garantita",
    desc: "Sei tutelato da apposite clausole contrattuali in caso di eventuali ritardi imputabili all'impresa.",
  },
  {
    icon: Calculator,
    title: "Pianificazione semplice e trasparente",
    desc: "Mettiamo a disposizione la possibilità di calcolare il preventivo online e di richiedere una consulenza iniziale gratuita; spesso i preventivi online sono anche superiori agli effettivi costi reali.",
  },
];

export default function PercheNoi() {
  return (
    <PageShell testid="perche-noi-page">
      {/* Intro */}
      <section className="relative overflow-hidden bg-brand-ink grain">
        <div className="absolute inset-0 opacity-20">
          <img src={IMAGES.living2} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <span className="text-brand-gold font-semibold tracking-[0.2em] uppercase text-sm">Perché noi</span>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold text-white mt-4 mb-8 leading-tight">
            Perché scegliere Ristrutturare Casa per la tua ristrutturazione a Napoli
          </h1>
          <div className="text-white/70 text-lg leading-relaxed space-y-5">
            <p>
              Ristrutturare casa è uno dei momenti più importanti e delicati: significa dare forma ai propri desideri
              e trasformare uno spazio nel luogo ideale in cui vivere. Tuttavia, per molti proprietari, la prospettiva
              di avviare un cantiere porta con sé lo stress legato a due grandi incognite: i tempi che si allungano e
              i costi che lievitano.
            </p>
            <p>
              La filosofia di Ristrutturare Casa Napoli nasce proprio per eliminare queste incertezze e restituire
              serenità a chi decide di rinnovare il proprio immobile. Filosofia che è nata perché, prima di essere
              imprenditori, siamo stati tutti clienti e sappiamo cosa voglia dire relazionarsi a chi ci promette cose
              che poi non mantiene.
            </p>
          </div>
        </div>
      </section>

      {/* Reasons */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-24">
        <h2 className="font-display text-2xl md:text-3xl font-extrabold text-brand-ink mb-12">
          I motivi per cui i clienti si affidano a noi
        </h2>
        <div className="space-y-6">
          {reasons.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              data-testid={`reason-${i}`}
              className="flex gap-5 md:gap-7 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-shadow p-7 md:p-9"
            >
              <div className="w-14 h-14 flex-shrink-0 rounded-xl bg-brand-gold/15 flex items-center justify-center">
                <r.icon className="text-brand-goldDark" size={26} />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold text-brand-ink mb-2">{r.title}</h3>
                <p className="text-brand-slate/70 leading-relaxed">{r.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contattaci */}
      <section className="bg-brand-ink">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white mb-5">Contattaci</h2>
          <p className="text-white/70 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Se desideri trasformare la tua casa con la certezza di tempi e costi rispettati, puoi richiedere una
            consulenza gratuita o calcolare il tuo preventivo online direttamente sul nostro sito.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#preventivo"
              data-testid="perche-preventivo-cta"
              className="group inline-flex items-center justify-center gap-2 bg-brand-gold hover:bg-brand-goldDark text-brand-ink font-bold px-8 py-4 rounded-full transition-colors"
            >
              Calcola il preventivo online
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="/contatti"
              data-testid="perche-contatti-cta"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white text-white font-semibold px-8 py-4 rounded-full transition-colors"
            >
              <MessageCircle size={18} /> Richiedi consulenza gratuita
            </a>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
